import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";
import SignInPromptModal from "../components/SignInPromptModal";
import {
  ChevronRight,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  Compass,
} from "lucide-react";
import BackBar from "../components/BackBar";

/* ─────────────────── Block context catalog ─────────────────── */
/**
 * Each context defines a streetscape composition: 4 neighbor silhouettes
 * (drawn from the same vocabulary as our backdrop SVGs) plus the "permitted"
 * setback range that defines the block's rhythm.
 */
const CONTEXTS = {
  "near-eastside": {
    id: "near-eastside",
    label: "Near Eastside",
    sub: "Porched residential",
    setback_min: 10,
    setback_max: 18,
    rhythm: "porched front, 10-18 ft setbacks",
    neighbors: [
      { side: "L", x: 120,  w: 200, h: 200, roof: "gable", porch: true },
      { side: "L", x: 340,  w: 220, h: 220, roof: "hip",   porch: true },
      { side: "R", x: 880,  w: 220, h: 210, roof: "gable", porch: true },
      { side: "R", x: 1120, w: 180, h: 200, roof: "hip",   porch: true },
    ],
  },
  "martindale-historic": {
    id: "martindale-historic",
    label: "Martindale-Brightwood",
    sub: "Historic block",
    setback_min: 18,
    setback_max: 25,
    rhythm: "deep front yards, 18-25 ft setbacks",
    neighbors: [
      { side: "L", x: 100, w: 240, h: 240, roof: "gable", porch: true },
      { side: "L", x: 360, w: 240, h: 240, roof: "gable", porch: true },
      { side: "R", x: 880, w: 240, h: 240, roof: "gable", porch: true },
      { side: "R", x: 1140, w: 200, h: 240, roof: "gable", porch: true },
    ],
  },
  "modern-infill": {
    id: "modern-infill",
    label: "Modern infill",
    sub: "Shed roofs",
    setback_min: 8,
    setback_max: 15,
    rhythm: "shed roofs, 8-15 ft setbacks",
    neighbors: [
      { side: "L", x: 100, w: 220, h: 230, roof: "shed", porch: false },
      { side: "L", x: 340, w: 220, h: 220, roof: "shed", porch: false },
      { side: "R", x: 880, w: 220, h: 220, roof: "shed", porch: false },
      { side: "R", x: 1120, w: 200, h: 230, roof: "shed", porch: false },
    ],
  },
  "narrow-lot": {
    id: "narrow-lot",
    label: "Narrow-lot row",
    sub: "30 ft frontages",
    setback_min: 5,
    setback_max: 12,
    rhythm: "tight frontages, 5-12 ft setbacks",
    neighbors: [
      { side: "L", x: 80,  w: 150, h: 230, roof: "gable", porch: true },
      { side: "L", x: 250, w: 150, h: 230, roof: "gable", porch: true },
      { side: "L", x: 420, w: 150, h: 230, roof: "shed",  porch: false },
      { side: "R", x: 800, w: 150, h: 230, roof: "gable", porch: true },
      { side: "R", x: 970, w: 150, h: 230, roof: "shed",  porch: false },
      { side: "R", x: 1140, w: 150, h: 230, roof: "gable", porch: true },
    ],
  },
};

const CONTEXT_ORDER = [
  "near-eastside",
  "martindale-historic",
  "modern-infill",
  "narrow-lot",
];

// Map design typology → subject silhouette spec
function subjectSilhouette(typology, lotWidth) {
  // Scale body width by lot width: 30/40/50 → 240/300/360 px
  const w = lotWidth === 30 ? 240 : lotWidth === 40 ? 300 : 360;
  const base = {
    w,
    door_w: 28,
    window_h: 28,
  };
  switch (typology) {
    case "duplex":
      return { ...base, h: 240, roof: "gable", doors: 2, porch: true };
    case "adu":
      return { ...base, w: Math.min(w, 220), h: 180, roof: "shed", doors: 1, porch: false };
    case "cottage_missing_middle":
      return { ...base, h: 200, roof: "gable", doors: 1, porch: true };
    case "single_family":
    default:
      return { ...base, h: 230, roof: "gable", doors: 1, porch: true };
  }
}

/* ─────────────────── House silhouette renderer ─────────────────── */

function HouseSVG({ x, w, h, roof, porch, ground = 460, fill = "#FBFAFD", stroke = "#1B1F4B", strokeW = 2 }) {
  const top = ground - h;
  const parts = [];
  // Body
  parts.push(
    <rect key="body" x={x} y={top} width={w} height={h} fill={fill} stroke={stroke} strokeWidth={strokeW} />
  );
  // Two windows
  parts.push(
    <rect key="w1" x={x + 14} y={top + 56} width={22} height={26} fill="none" stroke={stroke} strokeWidth={strokeW * 0.6} />
  );
  parts.push(
    <rect key="w2" x={x + w - 36} y={top + 56} width={22} height={26} fill="none" stroke={stroke} strokeWidth={strokeW * 0.6} />
  );
  // Center door
  parts.push(
    <rect key="d" x={x + w / 2 - 12} y={ground - 50} width={24} height={50} fill="none" stroke={stroke} strokeWidth={strokeW * 0.7} />
  );
  // Roof
  if (roof === "gable") {
    parts.push(
      <path key="r" d={`M${x - 6} ${top} L${x + w / 2} ${top - 42} L${x + w + 6} ${top}`} fill="none" stroke={stroke} strokeWidth={strokeW} strokeLinejoin="round" />
    );
  } else if (roof === "shed") {
    parts.push(
      <path key="r" d={`M${x - 6} ${top + 22} L${x + w + 6} ${top - 22} L${x + w + 6} ${top} L${x - 6} ${top} Z`} fill="none" stroke={stroke} strokeWidth={strokeW} />
    );
  } else if (roof === "hip") {
    parts.push(
      <path key="r" d={`M${x + 10} ${top} L${x + 30} ${top - 26} L${x + w - 30} ${top - 26} L${x + w - 10} ${top} Z`} fill="none" stroke={stroke} strokeWidth={strokeW} />
    );
  }
  if (porch) {
    parts.push(
      <line key="p" x1={x - 4} y1={ground - 24} x2={x + w + 4} y2={ground - 24} stroke="#5B6196" strokeWidth={1} />
    );
  }
  return <g>{parts}</g>;
}

/* ─────────────────── Subject design silhouette ─────────────────── */

function SubjectSVG({ x, sil, ground = 460 }) {
  const top = ground - sil.h;
  return (
    <g>
      {/* Body — slightly darker fill to read as the SUBJECT, not a neighbor */}
      <rect x={x} y={top} width={sil.w} height={sil.h} fill="#FBFAFD" stroke="#B14E2F" strokeWidth={2.5} />
      {/* Two windows */}
      <rect x={x + 18} y={top + 60} width={26} height={32} fill="none" stroke="#B14E2F" strokeWidth={1.6} />
      <rect x={x + sil.w - 44} y={top + 60} width={26} height={32} fill="none" stroke="#B14E2F" strokeWidth={1.6} />
      {/* Upper-floor dormer/window for taller silhouettes */}
      {sil.h >= 220 && (
        <rect x={x + sil.w / 2 - 14} y={top + 30} width={28} height={22} fill="none" stroke="#B14E2F" strokeWidth={1.6} />
      )}
      {/* Doors */}
      {sil.doors === 2 ? (
        <>
          <rect x={x + sil.w / 2 - 36} y={ground - 56} width={28} height={56} fill="none" stroke="#B14E2F" strokeWidth={2} />
          <rect x={x + sil.w / 2 + 8} y={ground - 56} width={28} height={56} fill="none" stroke="#B14E2F" strokeWidth={2} />
        </>
      ) : (
        <rect x={x + sil.w / 2 - 14} y={ground - 56} width={28} height={56} fill="none" stroke="#B14E2F" strokeWidth={2} />
      )}
      {/* Roof */}
      {sil.roof === "gable" ? (
        <path d={`M${x - 8} ${top} L${x + sil.w / 2} ${top - 48} L${x + sil.w + 8} ${top}`} fill="none" stroke="#B14E2F" strokeWidth={2.5} strokeLinejoin="round" />
      ) : (
        <path d={`M${x - 6} ${top + 22} L${x + sil.w + 6} ${top - 22} L${x + sil.w + 6} ${top} L${x - 6} ${top} Z`} fill="none" stroke="#B14E2F" strokeWidth={2.5} />
      )}
      {sil.porch && (
        <line x1={x - 6} y1={ground - 24} x2={x + sil.w + 6} y2={ground - 24} stroke="#B14E2F" strokeWidth={1.3} />
      )}
    </g>
  );
}

/* ─────────────────── Streetscape composition ─────────────────── */

function Streetscape({ context, sil, setbackFt }) {
  const VB_W = 1400;
  const VB_H = 580;
  const ground = 460;
  // Subject is anchored at x=620, scaled by sil.w
  const subjectX = (VB_W - sil.w) / 2;
  // Setback dimension line — shown in survey-blue, with the ft label
  const setbackPx = Math.max(20, setbackFt * 6); // 1 ft = 6 px feel
  const setbackY = ground + 26;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      width="100%"
      style={{ display: "block", backgroundColor: "#FBFAFD", borderRadius: 4, border: "1px solid #D9D8E4" }}
      role="img"
      aria-label="Block-fit streetscape preview"
      data-testid="streetscape-svg"
    >
      {/* Sky / paper */}
      <rect x="0" y="0" width={VB_W} height={VB_H} fill="#FBFAFD" />
      {/* Soft horizon band */}
      <rect x="0" y="340" width={VB_W} height="60" fill="#F3F2F6" />
      {/* Survey guides */}
      <line x1="0" y1="50"  x2={VB_W} y2="50"  stroke="#3A5563" strokeWidth="0.4" strokeDasharray="2 6" />
      <line x1="0" y1="280" x2={VB_W} y2="280" stroke="#3A5563" strokeWidth="0.4" strokeDasharray="2 6" />
      {/* Distant tree-line */}
      <path d={`M0 360 Q120 342 240 358 T520 352 T820 360 T1080 350 T${VB_W} 358`} fill="none" stroke="#5B6196" strokeWidth="1" />
      {/* Ground line */}
      <line x1="0" y1={ground} x2={VB_W} y2={ground} stroke="#1B1F4B" strokeWidth="2" />
      {/* Sidewalk */}
      <line x1="0" y1={ground + 56} x2={VB_W} y2={ground + 56} stroke="#9AA0C0" strokeWidth="1" />
      {/* Street centreline */}
      <line x1="0" y1={ground + 118} x2={VB_W} y2={ground + 118} stroke="#1B1F4B" strokeWidth="1" strokeDasharray="22 18" />

      {/* Neighbors */}
      {context.neighbors.map((n, i) => (
        <HouseSVG key={i} {...n} />
      ))}

      {/* Subject */}
      <SubjectSVG x={subjectX} sil={sil} />

      {/* Setback dimension line — survey blue, under the subject */}
      <g>
        <line
          x1={subjectX}
          y1={setbackY}
          x2={subjectX + setbackPx}
          y2={setbackY}
          stroke="#3A5563"
          strokeWidth="1"
        />
        {/* End ticks */}
        <line x1={subjectX} y1={setbackY - 6} x2={subjectX} y2={setbackY + 6} stroke="#3A5563" strokeWidth="1" />
        <line x1={subjectX + setbackPx} y1={setbackY - 6} x2={subjectX + setbackPx} y2={setbackY + 6} stroke="#3A5563" strokeWidth="1" />
        <text
          x={subjectX + setbackPx / 2}
          y={setbackY - 8}
          textAnchor="middle"
          fontFamily="Nunito Sans, sans-serif"
          fontSize="13"
          fontWeight="700"
          fill="#3A5563"
          style={{ letterSpacing: "0.04em" }}
        >
          {`← ${setbackFt} ft setback →`}
        </text>
      </g>

      {/* Context title strip — bottom-left */}
      <text
        x="24"
        y={VB_H - 16}
        fontFamily="Nunito Sans, sans-serif"
        fontSize="11"
        fontWeight="700"
        fill="#6C6B85"
        style={{ letterSpacing: "0.18em" }}
      >
        {context.label.toUpperCase()} · {context.sub.toUpperCase()}
      </text>
    </svg>
  );
}

/* ─────────────────────────── Page ─────────────────────────── */

export default function Visualizer() {
  const { designId } = useParams();
  const navigate = useNavigate();
  const { role, user } = useRole();
  const [design, setDesign] = useState(null);
  const [err, setErr] = useState(null);

  // Controls
  const [lotWidth, setLotWidth] = useState(40);
  const [setbackFt, setSetbackFt] = useState(15);
  const [contextId, setContextId] = useState("near-eastside");

  // Sign-in gating for the CTA
  const [signInOpen, setSignInOpen] = useState(false);
  const isOwner = role === "owner_builder" && user?.id;

  useEffect(() => {
    setDesign(null);
    setErr(null);
    api
      .get(`/marketplace/designs/${designId}`)
      .then((r) => {
        setDesign(r.data);
        // Default the lot width to design's minimum so it always fits initially
        if (r.data?.min_lot_width) setLotWidth(r.data.min_lot_width);
      })
      .catch((e) => setErr(e));
  }, [designId]);

  const context = CONTEXTS[contextId];
  const sil = useMemo(
    () => subjectSilhouette(design?.typology || "single_family", lotWidth),
    [design?.typology, lotWidth],
  );

  // Fit report booleans
  const fitLot = design ? lotWidth >= design.min_lot_width : true;
  const fitSetbackLegal = setbackFt >= 5; // city min (illustrative)
  const fitBlockRhythm =
    setbackFt >= context.setback_min && setbackFt <= context.setback_max;

  const handleFitStudy = () => {
    if (!isOwner) {
      setSignInOpen(true);
      return;
    }
    const q = new URLSearchParams({
      design: designId,
      intent: "fit-study",
      lot_width: String(lotWidth),
      setback: String(setbackFt),
      context: contextId,
    });
    navigate(`/owner/consultations/new?${q.toString()}`);
  };

  if (err) {
    return (
      <div style={{ color: "#A23B2E" }}>
        Could not open the visualizer.{" "}
        <Link to={`/marketplace/${designId}`} style={{ color: "#1B1F4B" }}>
          Back to design
        </Link>
      </div>
    );
  }
  if (!design) {
    return (
      <div style={{ color: "#6C6B85", fontStyle: "italic", padding: 24 }}>
        Loading visualizer…
      </div>
    );
  }

  return (
    <section data-testid="visualizer-page" className="animate-fade-in-up">
      <BackBar
        parent={{ to: `/marketplace/${designId}`, label: design.name }}
        trail={[
          { label: "Marketplace", to: "/marketplace" },
          { label: design.name, to: `/marketplace/${designId}` },
          { label: "Visualizer" },
        ]}
      />

      {/* Hero */}
      <header className="grid lg:grid-cols-[1fr_auto] gap-6 items-end mb-8">
        <div>
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }}
            />
            <span className="wn-label" style={{ color: "#8F3C23" }}>
              Block-fit visualizer
            </span>
          </div>
          <h1
            className="mt-3"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(36px, 4.6vw, 52px)",
              fontWeight: 500,
              lineHeight: 1.05,
              color: "#1B1F4B",
              letterSpacing: "-0.005em",
              margin: 0,
            }}
          >
            Does this design{" "}
            <span style={{ fontStyle: "italic", color: "#8F3C23" }}>
              belong on the block
            </span>
            ?
          </h1>
          <p
            className="mt-3 max-w-[700px]"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 17,
              lineHeight: 1.55,
            }}
          >
            A schematic block-fit check — not a final code review. Adjust lot
            width, front setback, and neighborhood context. The architect
            confirms exact fit during a paid fit study.
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="wn-label" style={{ color: "#6C6B85" }}>
            Design under test
          </div>
          <div
            className="mt-1"
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              color: "#1B1F4B",
              fontSize: 22,
            }}
          >
            {design.name}
          </div>
          <div className="wn-label" style={{ color: "#6C6B85" }}>
            {design.architect_firm}
          </div>
        </div>
      </header>

      {/* Streetscape SVG (full-width) */}
      <Streetscape context={context} sil={sil} setbackFt={setbackFt} />

      {/* Controls (3-column) */}
      <div
        className="mt-8 grid md:grid-cols-3"
        style={{ gap: 32 }}
        data-testid="viz-controls"
      >
        <ControlGroup label="Lot width">
          <ChipRow
            options={[
              { value: 30, label: "30 ft" },
              { value: 40, label: "40 ft" },
              { value: 50, label: "50 ft" },
            ]}
            value={lotWidth}
            onChange={setLotWidth}
            testid="ctrl-lot"
          />
        </ControlGroup>
        <ControlGroup label="Front setback">
          <ChipRow
            options={[
              { value: 10, label: "10 ft" },
              { value: 15, label: "15 ft" },
              { value: 20, label: "20 ft" },
            ]}
            value={setbackFt}
            onChange={setSetbackFt}
            testid="ctrl-setback"
          />
        </ControlGroup>
        <ControlGroup label="Block context">
          <ChipRow
            options={CONTEXT_ORDER.map((k) => ({
              value: k,
              label: CONTEXTS[k].label,
            }))}
            value={contextId}
            onChange={setContextId}
            testid="ctrl-context"
          />
        </ControlGroup>
      </div>

      {/* Fit report + Fit Study CTA */}
      <div
        className="mt-12 grid lg:grid-cols-[1.5fr_1fr]"
        style={{ gap: 32, alignItems: "stretch" }}
        data-testid="fit-report"
      >
        <div
          style={{
            border: "1px solid #D9D8E4",
            borderRadius: 4,
            backgroundColor: "#FBFAFD",
            padding: "22px 26px",
          }}
        >
          <div className="wn-label" style={{ color: "#8F3C23" }}>
            Block-fit report
          </div>
          <ul
            className="mt-4"
            style={{ listStyle: "none", padding: 0, margin: 0 }}
          >
            <FitRow
              ok={fitLot}
              label="Lot width"
              detail={
                fitLot
                  ? `Design needs ${design.min_lot_width} ft · ${lotWidth} ft selected · ${
                      lotWidth - design.min_lot_width
                    } ft to spare`
                  : `Design needs ${design.min_lot_width} ft · ${lotWidth} ft is too narrow`
              }
              first
            />
            <FitRow
              ok={fitSetbackLegal}
              label="Setback permitted"
              detail={
                fitSetbackLegal
                  ? `${setbackFt} ft meets the city's 5 ft minimum.`
                  : `${setbackFt} ft is below the city's 5 ft minimum.`
              }
            />
            <FitRow
              ok={fitBlockRhythm}
              label="Block rhythm"
              detail={
                fitBlockRhythm
                  ? `Matches ${context.label}'s ${context.rhythm}.`
                  : `${context.label} expects ${context.setback_min}-${context.setback_max} ft setbacks · ${setbackFt} ft is out of rhythm.`
              }
            />
          </ul>
          <div
            className="mt-5"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13,
              lineHeight: 1.55,
              borderTop: "1px solid #D9D8E4",
              paddingTop: 14,
            }}
          >
            Schematic check based on city minimums and block-pattern averages.
            Not a substitute for a code review. Variance, easements,
            setbacks on corner lots, and utility constraints are confirmed by
            the architect during a fit study.
          </div>
        </div>

        {/* Fit-study CTA card */}
        <div
          style={{
            backgroundColor: "#1B1F4B",
            color: "#FBFAFD",
            borderRadius: 4,
            padding: "26px 26px 24px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
          data-testid="fit-study-cta"
        >
          <div>
            <div
              className="wn-label"
              style={{ color: "rgba(251,250,253,0.65)" }}
            >
              Next step
            </div>
            <h3
              className="mt-3"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 500,
                fontSize: 26,
                lineHeight: 1.15,
                color: "#FBFAFD",
                letterSpacing: "-0.005em",
                margin: 0,
              }}
            >
              Request a{" "}
              <span style={{ fontStyle: "italic", color: "#E89B7B" }}>
                fit study
              </span>{" "}
              from {design.architect_firm}.
            </h3>
            <p
              className="mt-3"
              style={{
                fontFamily: '"Newsreader", Georgia, serif',
                fontStyle: "italic",
                color: "rgba(251,250,253,0.75)",
                fontSize: 14.5,
                lineHeight: 1.55,
              }}
            >
              The architect confirms exact fit on your parcel — setbacks,
              easements, code, and orientation. Paid, refundable if it cannot
              proceed.
            </p>
          </div>
          <div className="mt-5 flex flex-wrap gap-3 items-center">
            <button
              className="wn-btn"
              onClick={handleFitStudy}
              data-testid="fit-study-btn"
              style={{
                backgroundColor: "#B14E2F",
                color: "#FBFAFD",
                border: "1px solid #B14E2F",
                padding: "12px 22px",
                fontSize: 14,
              }}
            >
              <Compass size={16} />
              Request a Fit Study
            </button>
            <Link
              to={`/marketplace/${designId}`}
              className="wn-btn"
              data-testid="back-to-design"
              style={{
                backgroundColor: "transparent",
                color: "#FBFAFD",
                border: "1px solid rgba(251,250,253,0.4)",
                padding: "12px 22px",
                fontSize: 14,
              }}
            >
              <ArrowLeft size={16} />
              Back to design
            </Link>
          </div>
        </div>
      </div>

      {/* Roadmap hint — "real block context" is coming. Quiet
          hairline-top note that signals the next visualizer step
          without committing engineering hours yet. Visible directly
          under the schematic + fit-study cards. */}
      <div
        data-testid="visualizer-roadmap-hint"
        className="mt-8"
        style={{
          borderTop: "1px solid #D9D8E4",
          paddingTop: 18,
          display: "flex",
          alignItems: "flex-start",
          gap: 14,
          flexWrap: "wrap",
        }}
      >
        <div
          aria-hidden
          style={{
            flex: "0 0 auto",
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            fontSize: 9.5,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#3A4A35",
            background: "#E8EAD9",
            border: "1px solid #B8BFA0",
            borderRadius: 3,
            padding: "3px 8px",
            lineHeight: 1.3,
            marginTop: 2,
          }}
        >
          Coming soon
        </div>
        <div style={{ flex: "1 1 320px", minWidth: 0 }}>
          <div
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 500,
              fontSize: 17,
              color: "#1B1F4B",
              lineHeight: 1.3,
              letterSpacing: "-0.005em",
            }}
          >
            Real block context, drawn from the actual neighbours.
          </div>
          <p
            className="mt-2"
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 13.5,
              lineHeight: 1.6,
              maxWidth: 640,
              margin: 0,
            }}
          >
            The next iteration of this visualizer will replace the
            schematic neighbour outlines with the actual adjacent
            houses on the selected block — pulled from the City of
            Indianapolis open building-footprint dataset and rendered
            in the same line-drawing aesthetic. Same editorial story;
            real geometry behind every line.
          </p>
        </div>
      </div>

      <SignInPromptModal
        open={signInOpen}
        onOpenChange={setSignInOpen}
        action="Request a fit study"
      />
    </section>
  );
}

/* ─────────────────────────── Subcomponents ─────────────────────────── */

function ControlGroup({ label, children }) {
  return (
    <div>
      <div className="wn-label" style={{ color: "#8F3C23" }}>
        {label}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function ChipRow({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            onClick={() => onChange(o.value)}
            data-testid={`${testid}-${o.value}`}
            style={{
              padding: "7px 14px",
              borderRadius: 999,
              border: `1px solid ${active ? "#B14E2F" : "#D9D8E4"}`,
              backgroundColor: active ? "#B14E2F" : "transparent",
              color: active ? "#FBFAFD" : "#1B1F4B",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 600,
              fontSize: 13,
              cursor: "pointer",
              transition: "all 160ms ease",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function FitRow({ ok, label, detail, first }) {
  return (
    <li
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        alignItems: "start",
        columnGap: 14,
        padding: "12px 0",
        borderTop: first ? "none" : "1px solid #EEEDF3",
      }}
    >
      {ok ? (
        <CheckCircle2 size={20} color="#4F6149" strokeWidth={2} />
      ) : (
        <AlertTriangle size={20} color="#8F3C23" strokeWidth={2} />
      )}
      <div>
        <div
          style={{
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 700,
            color: "#1B1F4B",
            fontSize: 15,
            lineHeight: 1.2,
          }}
        >
          {label}{" "}
          <span
            style={{
              color: ok ? "#4F6149" : "#8F3C23",
              fontWeight: 600,
              marginLeft: 6,
            }}
          >
            {ok ? "✓" : "Review"}
          </span>
        </div>
        <div
          className="mt-1"
          style={{ color: "#272636", fontSize: 13.5, lineHeight: 1.5 }}
        >
          {detail}
        </div>
      </div>
    </li>
  );
}
